import { Search, X, SlidersHorizontal } from 'lucide-react';

export interface ReportFilters {
  search: string;
  startDate: string;
  endDate: string;
  meetingTypeId?: string;
}

interface ReportFilterBarProps {
  filters: ReportFilters;
  onChange: (filters: ReportFilters) => void;
  onReset?: () => void;
  /** Slot optionnel : sélecteur de type de réunion */
  meetingTypeSlot?: React.ReactNode;
  /** Nombre de résultats après filtrage */
  resultCount?: number;
  totalCount?: number;
  placeholder?: string;
}

export function defaultFilters(): ReportFilters {
  return { search: '', startDate: '', endDate: '', meetingTypeId: '' };
}

export function isFiltersActive(f: ReportFilters): boolean {
  return !!(f.search || f.startDate || f.endDate || f.meetingTypeId);
}

/** Filtre un tableau de rapports en mémoire selon les filtres actifs.
 *  `getDate` : extracteur de la date ISO du rapport
 *  `getSearchable` : extracteur des champs textuels pour la recherche
 */
export function applyFilters<T>(
  reports: T[],
  filters: ReportFilters,
  getDate: (r: T) => string,
  getSearchable: (r: T) => string[],
  getMeetingTypeId?: (r: T) => string
): T[] {
  return reports.filter(r => {
    // Filtre texte
    if (filters.search) {
      const term = filters.search.toLowerCase();
      const fields = getSearchable(r);
      if (!fields.some(f => f?.toLowerCase().includes(term))) return false;
    }
    // Filtre date début
    if (filters.startDate) {
      const d = getDate(r).slice(0, 10);
      if (d < filters.startDate) return false;
    }
    // Filtre date fin
    if (filters.endDate) {
      const d = getDate(r).slice(0, 10);
      if (d > filters.endDate) return false;
    }
    // Filtre type de réunion
    if (filters.meetingTypeId && getMeetingTypeId) {
      if (getMeetingTypeId(r) !== filters.meetingTypeId) return false;
    }
    return true;
  });
}

export function ReportFilterBar({
  filters,
  onChange,
  onReset,
  meetingTypeSlot,
  resultCount,
  totalCount,
  placeholder = 'Rechercher…',
}: ReportFilterBarProps) {
  const active = isFiltersActive(filters);
  const activeCount = [filters.search, filters.startDate, filters.endDate, filters.meetingTypeId]
    .filter(Boolean).length;

  const set = (partial: Partial<ReportFilters>) => onChange({ ...filters, ...partial });

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
      <div className="flex flex-wrap gap-3 items-end">

        {/* Recherche */}
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={filters.search}
            onChange={ev => onChange({ ...filters, search: ev.target.value })}
            placeholder={placeholder || 'Rechercher…'}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00665C]/30 focus:border-[#00665C] bg-gray-50"
          />
        </div>
        {/* Plage de dates */}
        <div className="flex gap-2 flex-1 min-w-[220px]">
          <div className="flex flex-col flex-1">
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Du</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={ev => onChange({ ...filters, startDate: ev.target.value })}
              className="w-full text-sm border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-[#00665C]/30 focus:border-[#00665C] bg-gray-50"
            />
          </div>
          <div className="flex flex-col flex-1">
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Au</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={ev => onChange({ ...filters, endDate: ev.target.value })}
              className="w-full text-sm border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-[#00665C]/30 focus:border-[#00665C] bg-gray-50"
            />
          </div>
        </div>

        {/* Slot optionnel : type de réunion */}
        {meetingTypeSlot && (
          <div className="flex flex-col min-w-[160px]">
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Type</label>
            {meetingTypeSlot}
          </div>
        )}

        {/* Compteur + Reset */}
        <div className="flex items-center gap-2 ml-auto">
          {totalCount !== undefined && (
            <span className="text-xs text-gray-500 whitespace-nowrap bg-gray-100 px-2.5 py-1.5 rounded-full font-medium">
              {resultCount ?? totalCount} / {totalCount}
            </span>
          )}
          {isFiltersActive(filters) && (
            <button
              onClick={onReset}
              title="Réinitialiser"
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 transition-colors px-2 py-2 rounded-lg hover:bg-red-50 border border-gray-200"
            >
              <X className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Réinit.</span>
            </button>
          )}
        </div>

      </div>
    </div>
  );
}